import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BeakerIcon } from '@heroicons/react/24/outline';

import {
	AttributeRow,
	TemplateBoolean,
	TemplateField,
} from '../../components/Attributes/Attributes_old';
import Slider from '../../components/Shell/Slider';
import LoadingSkeleton from '../../components/Shell/LoadingSkeleton';
import Tabs from '../../components/Tabs';
import DataTable from '../../components/DataTable';
import Button from '../../components/Button';

import useAsync from '../../hooks/useAsync';
import useModal from '../../hooks/useModal';

import { getConsumable } from '../../api/consumables';

const ViewConsumable = () => {
	const navigate = useNavigate();
	const { open: options } = useModal('viewConsumable');

	useEffect(() => {
		if (options?.id) {
			refreshConsumable({ id: options?.id });
		}
	}, [options?.id]);

	const {
		value: consumable,
		execute: refreshConsumable,
		status: isLoadingConsumable,
	} = useAsync(getConsumable, { immediate: false });

	return (
		<Slider
			name="viewConsumable"
			onClose={() => navigate(`/consumables/all`)}
			title={consumable?.name}
			form={true}
		>
			{isLoadingConsumable === 'pending' ? (
				<LoadingSkeleton />
			) : (
				<div>
					<div className="mt-2 mb-8 h-40 border border-gray-200 rounded-xl flex items-center justify-center">
						<BeakerIcon className="w-12 h-12 text-gray-200" />
					</div>

					<Tabs
						tabs={[
							{ name: 'General', key: 'general' },
							{ name: 'Inventory', key: 'inventory' },
							{ name: 'Settings', key: 'settings' },
						]}
					>
						{(key) => {
							if (key === 'general') {
								return (
									<div className="space-y-4">
										<AttributeRow name="Vendor">
											<span className="text-xs text-gray-500">
												{consumable?.vendor?.companyName || 'No vendor set'}
											</span>
										</AttributeRow>
										<AttributeRow name="Category">
											<span className="text-xs text-gray-500">
												{consumable?.category?.name || 'No category set'}
											</span>
										</AttributeRow>

										{consumable?.attributes?.map((attr) => (
											<AttributeRow name={attr?.attribute_field?.label}>
												<span className="text-xs text-gray-500">
													{attr?.attribute_field?.type === 'vendor'
														? attr?.vendor?.companyName
														: attr?.value}
												</span>
											</AttributeRow>
										))}
									</div>
								);
							}

							if (key === 'inventory') {
								return <DataTable />;
							}

							if (key === 'settings') {
								return (
									<div className="space-y-12 mt-8">
										<div className="grid grid-cols-1 gap-x-8 gap-y-4 pb-6">
											<div>
												<h2 className="text-sm font-semibold leading-7 text-gray-900">
													General Information
												</h2>
												<p className="mt-1 text-xs leading-6 text-gray-600">
													Basic information about this storage point
												</p>
											</div>
											<div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8">
												<TemplateField name="Name" />
											</div>
										</div>

										<div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-6">
											<div>
												<h2 className="text-sm font-semibold leading-7 text-gray-900">
													Delete Storage Point
												</h2>
												<p className="mt-1 text-xs leading-6 text-gray-600">
													Once you delete a storage point, there is no going
													back. Please be certain
												</p>
											</div>
											<div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8">
												<div>
													<Button
														type="danger"
														onClick={() =>
															openDeleteModal({
																title:
																	'Are you sure you want to delete this storage point?',
																form: (form, onChange) => (
																	<div className="py-4">
																		<TemplateBoolean
																			name="Reset inventory counts (if applicable)?"
																			onChange={(index, reset) => {
																				if (typeof onChange === 'function') {
																					onChange({
																						...form,
																						resetInventory: reset,
																					});
																				}
																			}}
																			value={form?.resetInventory}
																		/>
																		<p className="text-xxs mt-2">
																			By resetting the inventory count, you
																			remove all consumables attached to this
																			storage point
																		</p>
																	</div>
																),
																defaultForm: { resetInventory: true },
																confirmText: 'Delete Storage Point',
																onConfirm: (form) =>
																	onDeleteStoragePoint(
																		{
																			id,
																			resetInventory: form?.resetInventory,
																		},
																		() => {
																			closeDeleteModal();
																			navigate(
																				'/consumables/storage-points'
																			);
																		}
																	),
															})
														}
													>
														Delete Storage Point
													</Button>
												</div>
											</div>
										</div>
									</div>
								);
							}
						}}
					</Tabs>
				</div>
			)}
		</Slider>
	);
};

export default ViewConsumable;
